import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { customAlphabet } from 'nanoid'
import { getJWTToken } from 'utils'

const nanoId = customAlphabet('1234567890', 8)
const axiosInstance = axios.create({
  timeout: 90000,
})

// get 6 digit random number using crypto and then converting decimal number to be used by exponential algo
// to get number .899339 ,.39393929 etc
//https://nodejs.org/docs/latest/api/crypto.html#crypto_crypto_randomfillsync_buffer_offset_size
export const getRandomNumber = () => {
  let code = ''
  do {
    /* code += crypto.randomBytes(3).readUIntBE(0, 3) */
    code += Number(nanoId())
  } while (code.length < 6)

  return Number.parseInt(code.slice(0, 6)) / 1000000
}

// Get 8 character randoms string
export function randomString() {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz'
  const stringLength = 8
  let randomstring = ''
  for (let i = 0; i < stringLength; i++) {
    const charLength = chars.length
    const rnum = Math.floor(getRandomNumber() * charLength)
    randomstring += chars.substring(rnum, rnum + 1)
  }
  return randomstring
}

function axiosErrorhandling(error: any) {
  if (error?.response?.status >= 400) {
    throw new Error(JSON.stringify(error.response))
  } else {
    if (error?.message === 'Network Error') {
      throw new Error('ECONNABORTED')
    } else {
      throw new Error(error.code)
    }
  }
}

/**
 Set default fields to make api call.
 */
export const defaultOptions: AxiosRequestConfig = {
  method: 'get',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  data: '',
}

export async function getNewHeader() {
  let token = await getJWTToken()
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }
}

export async function makeRequest(
  uri: string,
  method: 'get' | 'post' | 'patch' | undefined,
  options = defaultOptions,
  isAuthRequest = true,
) {
  if (isAuthRequest) {
    //Refresh token if token creation time is 15 minutes before
    //refreshSessionToken()
    //set header
    const newHeader = await getNewHeader()
    options = Object.assign(options, newHeader)
  }
  options.url = uri
  options.method = method
  return axiosInstance(options)
    .then((response) => {
      //returns a json request.
      return response.data
    })
    .catch((error) => {
      axiosErrorhandling(error)
    })
}

/**
 Used for making get request to the api.
 */
export async function doGetRequest(
  uri: string,
  hasQueryString = false,
  isAuthRequest = true,
  options = defaultOptions,
  isRandomCode = true,
) {
  const $hasQueryString = hasQueryString ? '&&' : '?'
  const randomCode = randomString()
  const randomCodeQuery = isRandomCode
    ? `${$hasQueryString}rand=${randomCode}`
    : ''
  const $Url = `${uri}${randomCodeQuery}`
  return makeRequest($Url, 'get', options, isAuthRequest)
}

/**
 Used for making post request to the api.
 */
export function doPostRequest(
  uri: string,
  data: any,
  restWithCredentials = false,
  isAuthRequest = true,
  options = defaultOptions,
) {
  options.data = JSON.stringify(data)
  if (restWithCredentials) {
    options.withCredentials = false
  }
  return makeRequest(uri, 'post', options, isAuthRequest)
}

/**
 Used for making post request to the api.
 */
export function doPatchRequest(
  uri: string,
  data: any,
  restWithCredentials = false,
  isAuthRequest = true,
  options = defaultOptions,
) {
  options.data = JSON.stringify(data)
  if (restWithCredentials) {
    options.withCredentials = false
  }
  return makeRequest(uri, 'patch', options, isAuthRequest)
}

export async function doGetRequestMock(
  response: string,
): Promise<AxiosResponse<string>> {
  return {
    data: response,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {
      headers: {},
    } as InternalAxiosRequestConfig,
  }
}
